import { cloneElement, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import AdbIcon from '@mui/icons-material/Adb';
import { NavLink, useNavigate } from "react-router-dom";
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    useScrollTrigger
} from "@mui/material";
import { useAuth } from "../../store/auth-context";
import { usePackageModal } from "../../store/modal-context";
import WebOnly from "../platforms/WebOnly";
import AppOnly from "../platforms/AppOnly";
import PersonIcon from '@mui/icons-material/Person';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import LogoutIcon from '@mui/icons-material/Logout';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import Badge from '@mui/material/Badge';
import { useNotification } from "../../store/notification-context";
import AnnouncementBar from './AnnouncementBar';

const MobileNavigation = () => {
    const [open, setOpen] = useState(false);
    const toggleDrawer = (newOpen) => () => {
        setOpen(newOpen);
    };
    const { openModal } = usePackageModal();
    const { logout } = useAuth();

    const menuItems = [
        {
            icon: <AdbIcon />,
            title: 'Lista File',
            link: '/dashboard',
        }, {
            icon: <PersonIcon />,
            title: 'Scheda Psicologo',
            link: '/scheda-psicologo',
        }, {
            icon: <ReceiptLongIcon />,
            title: 'Pagamenti',
            link: '/billing',
        }, {
            icon: <MoreTimeIcon />,
            title: 'Pacchetti',
            onClick: () => {
                openModal();
            }
        }, {
            icon: <LogoutIcon />,
            title: 'Logout',
            onClick: () => {
                logout();
            }
        }
    ];

    return <>
        <Box sx={{ width: 250, display: 'flex' }} role="presentation" onClick={toggleDrawer(false)}>
            <Drawer open={open} onClose={toggleDrawer(false)}>
                <List>
                    {menuItems.map(({ title, icon, link, onClick }) => (
                        <ListItem key={title} disablePadding onClick={onClick}>
                            <NavLink
                                key={title}
                                to={link}
                                style={({ isActive }) => {
                                    return (isActive) ? {
                                        fontWeight: 'bold',
                                        textDecoration: 'none',
                                        width: '100%'
                                    } : {
                                        textDecoration: 'none',
                                        width: '100%'
                                    }
                                }}
                            >
                                <ListItemButton>
                                    <ListItemIcon>
                                        {icon}
                                    </ListItemIcon>
                                    <ListItemText primary={title} />
                                </ListItemButton>
                            </NavLink>
                        </ListItem>
                    ))}
                </List>
            </Drawer>
        </Box>
        <Box sx={{ background: (theme) => theme.palette.primary.main, display: 'flex' }}>
            <Container maxWidth="xl">
                <Toolbar disableGutters>
                    <Box>
                        <IconButton
                            size="large"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={toggleDrawer(true)}
                            color="inherit"
                        >
                            <MenuIcon />
                        </IconButton>
                    </Box>
                    <Stack alignItems={'center'} justifyContent={'center'} sx={{ flexGrow: 1 }}>
                        <img alt="Logo Sbobbi"
                            height="40"
                            src="/logo-hor-white.svg" />
                    </Stack>
                    <NotificationsMenu />
                    <UserMenu />
                </Toolbar>
            </Container>
        </Box>
    </>
}

const DesktopNavigation = ({ onCloseNav }) => {
    const menuItems = [
        {
            icon: <AdbIcon />,
            title: 'Lista File',
            link: '/dashboard',
        }, {
            icon: <ReceiptLongIcon />,
            title: 'Pagamenti',
            link: '/billing',
        }
    ];
    return <>
        <Stack>
            <Container maxWidth="xl">
                <Toolbar>
                    <Stack alignItems={'center'} justifyContent={'center'} mr={4}>
                        <img alt="Logo Sbobbi"
                            height="40"
                            src="/logo-hor-white.svg" />
                    </Stack>
                    <Box sx={{ flexGrow: 1, display: 'flex' }}>
                        {menuItems.map((page) => (
                            <NavLink
                                key={page.title}
                                to={page.link}
                                style={({ isActive }) => {
                                    return (isActive) ? {
                                        fontWeight: 'bold',
                                        textDecoration: 'none',
                                    } : {
                                        textDecoration: 'none',
                                    }
                                }}
                            >
                                <Button
                                    key={page.title}
                                    onClick={onCloseNav}
                                    sx={{ my: 2, fontWeight: 'inherit', color: 'white', display: 'block' }}
                                >
                                    {page.title}
                                </Button>
                            </NavLink>
                        ))}
                    </Box>
                    <NotificationsMenu />
                    <UserMenu />
                </Toolbar>
            </Container>
        </Stack>
    </>
}

const NotificationsMenu = () => {
    const [anchorElNotifications, setAnchorElNotifications] = useState(null);
    const {
        unreadCount,
        inboxNotifications,
        loadInboxNotifications,
        deleteInboxNotification,
        clearInboxNotifications
    } = useNotification();
    const navigate = useNavigate();

    const openNotificationsMenu = async (event) => {
        setAnchorElNotifications(event.currentTarget);
        await loadInboxNotifications();
    };

    const closeNotificationsMenu = () => {
        setAnchorElNotifications(null);
    };

    const openFileNotification = async (notificationId, fileId) => {
        await deleteInboxNotification(notificationId);
        closeNotificationsMenu();

        if (fileId) {
            navigate(`/file/${fileId}`);
        }
    };

    return (
        <Box sx={{ flexGrow: 0, mr: 1 }}>
            <Tooltip title="Notifiche">
                <IconButton color="inherit" onClick={openNotificationsMenu}>
                    <Badge badgeContent={unreadCount} color="error">
                        <NotificationsNoneIcon />
                    </Badge>
                </IconButton>
            </Tooltip>
            <Menu
                sx={{ mt: '45px' }}
                anchorEl={anchorElNotifications}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(anchorElNotifications)}
                onClose={closeNotificationsMenu}
            >
                {inboxNotifications.length === 0 && (
                    <MenuItem disabled>
                        <Typography sx={{ textAlign: 'center' }}>Nessuna notifica</Typography>
                    </MenuItem>
                )}
                {inboxNotifications.map((notification) => (
                    <MenuItem key={notification.id} onClick={() => openFileNotification(notification.id, notification.fileId)}>
                        <Stack spacing={0.5}>
                            <Typography fontWeight='bold'>{notification.title}</Typography>
                            <Typography variant='body2'>{notification.message}</Typography>
                        </Stack>
                    </MenuItem>
                ))}
                {inboxNotifications.length > 0 && (
                    <MenuItem onClick={clearInboxNotifications}>
                        <Typography color="error">Svuota notifiche</Typography>
                    </MenuItem>
                )}
            </Menu>
        </Box>
    );
};

const UserMenu = () => {
    const [anchorElUser, setAnchorElUser] = useState(null);
    const packageModalCtx = usePackageModal();
    const navigate = useNavigate();
    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };
    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };
    const authCtx = useAuth();

    const settings = [
        {
            label: 'Scheda Psicologo',
            onClick: () => {
                handleCloseUserMenu();
                navigate('/scheda-psicologo');
            }
        },
        {
            label: 'Pagamenti',
            onClick: () => {
                handleCloseUserMenu();
                navigate('/billing');
            }
        },
        {
            label: 'Acquista Pacchetti',
            onClick: () => {
                handleCloseUserMenu();
                packageModalCtx.openModal();
            }
        },
        {
            label: 'Logout',
            onClick: () => {
                handleCloseUserMenu();
                authCtx.logout();
            }
        }
    ];

    return <>
        <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Open settings">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar alt={authCtx.user ? authCtx.user.name : "User"} src="/static/images/avatar/2.jpg" />
                </IconButton>
            </Tooltip>
            <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
            >
                {settings.map((setting) => (
                    <Box key={setting.label} onClick={handleCloseUserMenu}>
                        <MenuItem key={setting.label} onClick={setting.onClick}>
                            <Typography sx={{ textAlign: 'center' }}>{setting.label}</Typography>
                        </MenuItem>
                    </Box>
                ))}
            </Menu>
        </Box>
    </>;
}

const Navigation = () => {
    const [anchorElNav, setAnchorElNav] = useState(null);

    const handleOpenNavMenu = (event) => {
        setAnchorElNav(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    const ElevationScroll = (props) => {
        const { children, window } = props;
        const trigger = useScrollTrigger({
            disableHysteresis: true,
            threshold: 0,
        });

        return children
            ? cloneElement(children, {
                elevation: trigger ? 1 : 0,
            })
            : null;
    }

    return (
        <>
            <AnnouncementBar text="Le funzionalità AI sono momentaneamente non disponibili!" />
            <WebOnly>
                <AppBar position="sticky">
                    <DesktopNavigation anchorNav={anchorElNav} onCloseNav={handleCloseNavMenu} onOpenNav={handleOpenNavMenu} />
                </AppBar>
            </WebOnly>
            <AppOnly>
                <ElevationScroll>
                    <AppBar>
                        <MobileNavigation anchorNav={anchorElNav} onCloseNav={handleCloseNavMenu} onOpenNav={handleOpenNavMenu} />
                    </AppBar>
                </ElevationScroll>
                <Box height={56} />
            </AppOnly>
        </>
    );
}

export default Navigation;
